'use client'

import { useState, useEffect, use } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { gql, useMutation } from '@apollo/client'
import { PageContainer } from '@/components/ui/page-container'
import { PageBackButton } from '@/components/ui/page-back-button'
import { sanitizeHtml, handleExternalContentClick } from '@/lib/htmlLinks'
import React from 'react'
import {
  MoreVertical,
  Heart,
  Share2,
  ChevronLeft,
  ChevronRight,
  Star,
  Edit,
  Trash,
  Flag,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  useGetPostDetailQuery,
  useListingDetailQuery,
  useLikePostMutation,
  useAddCommentMutation,
  useDeletePostMutation,
  useGetPostLikesQuery,
  useDeleteCommentMutation,
  useLogShareMutation,
  useUserProfileQuery,
} from 'graphql/generated/graphql'
import { buildShareUrl } from '@/lib/sharing'
import useUserType from '@/hook/useUserType'
import useFormatDate from '@/hook/useFormatDate'
import { ProfileUtils } from 'utils/profile'
import { getUserId } from '@/lib/utils'
import { toast } from 'react-toastify'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useSelector } from 'react-redux'
import { RootState } from 'features/store'
import { useAuth } from '@/lib/authContext'
import { isUserAvailable } from 'helpers/authUser'
import { AuthRequired } from '../authRequired'
import { PostShareModal } from './PostShareModal'
import { postHasHeroImage } from '@/lib/postImage'

interface Comment {
  id: string
  content: string
  createdAt: string
  user: {
    user_id?: number
    full_name?: string
    user_type?: string
    profile_photo_resource?: string
    default_listing?: {
      photo?: string
    }
  }
}

function getCommentAvatarSrc(user: Comment['user']): string {
  const userType = user.user_type?.toLowerCase()
  if (userType === 'producer') {
    return user.default_listing?.photo || '/assets/icons/tractor.svg'
  }
  if (userType === 'market') {
    return user.default_listing?.photo || '/assets/icons/Market.svg'
  }
  return user.profile_photo_resource || '/assets/avatar/user-default.svg'
}
// @ts-ignore
export default function PostDetail({ slug, initialPost }: { slug: string; initialPost?: any }) {
  const router = useRouter()
  // @ts-ignore
  //const { slug } = use(params) // Unwrap params using use()

  const [commentText, setCommentText] = useState('')
  const [activeCommentMenu, setActiveCommentMenu] = useState<string | null>(
    null
  )
  const [isLiked, setIsLiked] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const { checkAuth } = useAuth()

  // Queries
  const { data, loading, error, refetch } = useGetPostDetailQuery({
    // @ts-ignore
    variables: { slug },
    fetchPolicy: 'no-cache'
  })

  const { data: listing } = useListingDetailQuery({
    skip: !data?.getPost?.user?.default_listing_id,
    variables: {
      listingId: data?.getPost?.user?.default_listing_id || 0,
    },
  })

  const { data: likesData, refetch: refetchPostLikes } = useGetPostLikesQuery({
    skip: !data?.getPost?.id,
    variables: { postId: data?.getPost?.id || 0 },
  })

  // Mutations
  const [likePost] = useLikePostMutation()
  const [addComment] = useAddCommentMutation()
  const [deletePost] = useDeletePostMutation()
  const [deleteComment] = useDeleteCommentMutation()
  const [logShare] = useLogShareMutation()
  const [trackEvent] = useMutation(gql`mutation TrackEvent($input: TrackEventInput!) { trackEvent(input: $input) }`)

  const postData = data?.getPost ?? initialPost
  const currentUserId = getUserId()
  const isPostOwner = currentUserId === postData?.user?.user_id
  const user = useSelector((state: RootState) => state?.culineerAuth?.user)
  const userAvailable = isUserAvailable(user)
  // login-time redux `user` can carry a stale/expired presigned default_listing.photo
  // URL; fetch the same way the (always-correct) top-nav avatar does, in navbar.tsx.
  const { data: userProfileData } = useUserProfileQuery({
    variables: { slug: user?.slug },
    skip: !user?.slug,
  })
  const ownAvatarUser = userProfileData?.userProfile ?? user

  // Track feed post view
  useEffect(() => {
    if (postData?.id) {
      trackEvent({ variables: { input: {
        event_type: 'feed_post_viewed',
        entity_type: 'post',
        entity_id: String(postData.id),
      }}}).catch(() => {})
    }
  }, [postData?.id])

  // Initialize like status and count
  useEffect(() => {
    if (postData) {
      // Check if current user has liked the post
      const userLiked = likesData?.getPostLikes?.some(
        (like) => like.user_id === currentUserId
      )
      setIsLiked(!!userLiked)
    }
  }, [postData, likesData, currentUserId])

  const handleLike = async () => {
  if (!checkAuth()) return
    if (!postData) return

    try {
      // Optimistic update
      // setIsLiked(!isLiked)

      await likePost({
        variables: { postId: postData.id },
      })

      // Refetch to ensure data is in sync
      refetch()
      refetchPostLikes()
    } catch (error) {
      // Revert on error
      setIsLiked(!isLiked)
      toast.error('Failed to like post')
    }
  }

  const handleAddComment = async () => {
    if (!commentText.trim() || !postData) return

    try {
      await addComment({
        variables: {
          postId: postData?.id,
          content: commentText,
        },
      })
      setCommentText('')
      toast.success('Comment added')
      refetch() // Refresh comments
    } catch (error) {
      toast.error('Failed to add comment')
    }
  }

  const isBusiness = user?.user_type === 'producer' || user?.user_type === 'market'
  const refSlug = isBusiness ? (user?.default_listing?.slug || user?.slug) : user?.slug

  const handleEditPost = (e) => {
    e.stopPropagation()
    if (!postData) return
    router.push(`/posts/edit/${postData.slug}`)
  }

  const handleDeletePost = async () => {
    if (!postData) return

    try {
      await deletePost({
        variables: { postID: postData.id },
      })
      toast.success('Post deleted successfully')
      router.back()
    } catch (error) {
      toast.error(error)
    } finally {
      setShowDeleteConfirm(false)
    }
  }

  const handleDeleteComment = async (id: any) => {
    if (!postData) return

    try {
      await deleteComment({
        variables: { commentID: id },
      })
      toast.success('Comment deleted successfully')
      refetch()
    } catch (error) {
      console.error('Error deleting comment:', error)
      toast.error(error.message)
    } finally {
      setShowDeleteConfirm(false)
    }
  }

  const renderPostMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-2 rounded-full hover:bg-gray-100">
          <MoreVertical className="w-5 h-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-40">
        {isPostOwner ? (
          <>
            <DropdownMenuItem
              onClick={handleEditPost}
               className="flex items-center gap-2 cursor-pointer"
            >
               <Edit className="w-4 h-4" />
              <span>Edit Post</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 text-red-500 cursor-pointer"
            >
              <Trash className="w-4 h-4" />
              <span>Delete Post</span>
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem
            className="flex items-center gap-2 text-red-500 cursor-pointer"
            onClick={() => toast('User reported')}
          >
            <Flag className="w-4 h-4" />
            <span>Report User</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const renderCommentMenu = (comment: Comment) => {
    const isCommentOwner = currentUserId === comment?.user?.user_id
    return (
      <DropdownMenu
        open={activeCommentMenu === comment?.id}
        onOpenChange={(open) => setActiveCommentMenu(open ? comment?.id : null)}
      >
        <DropdownMenuTrigger asChild>
        <button className="w-8 h-8">
        <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-40">
          {isCommentOwner ? (
            <DropdownMenuItem
              onClick={() => handleDeleteComment(comment?.id)}
              className="text-red-500"
            >
              Delete Comment
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              className="text-red-500"
              onClick={() => toast('User reported')}
            >
              Report User
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const renderStars = (rating: number, total = 5) => {
    return Array(total)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
          }`}
        />
      ))
  }

  if (loading && !initialPost) return <div className="flex justify-center py-8">Loading...</div>
  if (error && !initialPost)
    return (
      <div className="p-4 text-red-500">
        Error loading post: {error.message}
      </div>
    )
  if (!postData) return <div className="p-4">Post not found</div>

  const isBlogPost = postData.group === 'admin'

  // Show preview for non-authenticated users
  // if (!userAvailable) {
  //   return <BlogPostPreview post={{
  //     id: postData.id,
  //     title: postData.title || '',
  //     content: postData.content,
  //     created_at: postData.created_at,
  //     post_medias: postData.post_medias,
  //     user: postData.user ? {
  //       full_name: postData.user.full_name,
  //       profile_photo_resource: postData.user.profile_photo_resource,
  //       default_listing: postData.user.default_listing
  //     } : undefined
  //   }} />
  // }

  return (
    <PageContainer className="mt-4 overflow-x-visible rounded-lg bg-white py-2">
      <div className="px-4 pt-4 xs:px-2">
        <PageBackButton fallbackHref={isBlogPost ? '/blog' : '/community'} />
      </div>
      {/* Header */}
      <div className="border-b">
        <div className="flex items-center justify-between p-4 xs:p-2">
          <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => {
            const userType = postData?.user?.user_type?.toLowerCase()
            const listingSlug = postData?.user?.default_listing?.slug
            if ((userType === 'producer' || userType === 'market') && listingSlug) {
              router.push(`/${userType}/${listingSlug}`)
            } else {
              router.push(`/member/${postData?.user?.slug}`)
            }
          }}>
          <div className="relative w-12 h-12 overflow-hidden rounded-full">
            <Image
                src={ProfileUtils.getImage(
                  postData?.user,
                  // @ts-ignore
                  listing?.listingDetail
                ) || '/assets/avatar/user-default.svg'}
                alt={ProfileUtils.getName(
                  postData?.user,
                  // @ts-ignore
                  listing?.listingDetail
                )}
                fill
                sizes="48px"
                unoptimized
                className="object-cover"
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {/* @ts-ignore */}
                {ProfileUtils.getName(postData?.user, listing?.listingDetail)}
              </h2>
              <span className="rounded bg-blue-50 px-2 py-0.5 text-sm text-blue-600">
                {useUserType(postData?.user?.user_type)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {userAvailable && renderPostMenu()}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row">
        <div className={`p-2 md:p-4 lg:p-8 xs:p-1 border-r w-full ${isBlogPost ? 'md:flex-1 md:min-w-0' : 'md:w-2/3 lg:w-2/3'}`}>
          {/* Post image */}
          {postData.post_medias?.length > 0 && (
            <div className="relative mb-6 aspect-video w-full max-w-[1200px] overflow-hidden rounded-lg bg-[#F7F7F7]">
              <Image
                src={postData?.post_medias[0]?.url || '/placeholder.svg'}
                alt="Post image"
                fill
                sizes="(max-width: 1440px) calc(100vw - 64px), 1200px"
                unoptimized
                className="object-cover"
                priority
              />
            </div>
          )}

          {/* Post content */}
          <div className="mb-6">
            <h1 className="mb-2 text-2xl font-bold break-words xs:text-base">{postData?.title}</h1>
            <div
              className="prose text-gray-800 max-w-none ProseMirror break-words xs:text-xs"
              style={{ wordBreak: 'break-word' }}
              onClick={handleExternalContentClick}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(postData?.content ?? '') }}
            />
          </div>

          {/* Like and Share buttons */}
          <div className="flex py-2 mb-6 border-t border-b xs:py-1">
            <button
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-3 xs:py-2 hover:bg-gray-100 ${
                isLiked ? 'text-[#85C349]' : 'text-gray-600'
              } xs:text-xs`}
              onClick={handleLike}
            >
              {isLiked ? (
                <Heart className="h-5 w-5 fill-[#85C349]" />
              ) : (
                <Heart className="w-5 h-5" />
              )}
              <span>Like ({postData?.likeCount})</span>
            </button>
            <button
              onClick={() => setShareModalOpen(true)}
              className="flex items-center justify-center flex-1 gap-2 py-3 xs:py-2 text-gray-600 rounded-lg hover:bg-gray-100 xs:text-xs"
              >
                <Share2 className="w-5 h-5" />
                <span>Share</span>
              </button>
            </div>  

          {/* Comments section */}
         {userAvailable ? (
           <div className="mb-6">
           <h3 className="mb-4 text-lg font-semibold xs:text-base">
             Comments ({postData?.comments?.length})
           </h3>
    
           <div className="space-y-6">
             {postData?.comments?.map((comment) => (
               <div key={comment.id} className="relative">
                 <div className="flex gap-3 xs:gap-2">
                   <div className="flex-shrink-0 w-10 h-10 overflow-hidden rounded-full xs:w-8 xs:h-8">
                     <Image
                       src={getCommentAvatarSrc(comment.user)}
                       alt={comment.user.full_name}
                       width={40}
                       height={40}
                       className="rounded-full object-cover"
                     />
                   </div>
                   <div className="flex-1">
                     <div className="flex justify-between">
                       <h4 className="font-medium xs:text-xs">
                         {comment.user.full_name}
                       </h4>
                       <div className="flex items-center gap-2 xs:gap-1">
                         <span className="text-sm text-gray-500 xs:text-xs">
                           {/* @ts-ignore */}
                           {useFormatDate(comment.createdAt)}
                         </span>
                         {/* @ts-ignore */}
                         {renderCommentMenu(comment)}
                       </div>
                     </div>
                     <p className="mt-1 text-gray-800 xs:text-xs">{comment.content}</p>
                   </div>
                 </div>
               </div>
             ))}

             {/* Comment input */}
             <div className="flex gap-3 mt-6 xs:gap-2">
               <div className="flex-shrink-0 w-10 h-10 overflow-hidden rounded-full xs:w-8 xs:h-8">
                 <Image
                   src={getCommentAvatarSrc(ownAvatarUser)}
                   alt="Your profile"
                   width={40}
                   height={40}
                   className="rounded-full"
                 />
               </div>
               <div className="flex flex-1">
                 <input
                   type="text"
                   placeholder="Write comment..."
                   className="flex-1 rounded-l-full border px-4 py-2 focus:outline-none focus:ring-1 focus:ring-[#85C349] xs:px-2 xs:py-1 xs:text-xs"
                   value={commentText}
                   onChange={(e) => setCommentText(e.target.value)}
                   onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                 />
                 <button
                   className="rounded-r-full bg-[#85C349] px-6 text-white xs:px-3 xs:text-xs xs:py-1"
                   onClick={handleAddComment}
                   disabled={!commentText.trim()}
                 >
                   Send
                 </button>
               </div>
             </div>
           </div>
         </div>
         ) : (
          <AuthRequired tab="post" />
         )
        }
        </div>

        <div className={`p-2 md:p-4 lg:p-8 w-full xs:p-1 ${isBlogPost ? 'md:w-64 lg:w-72 md:shrink-0' : 'md:w-1/3 lg:w-1/3'}`}>
         <div className="sticky top-0">
          {postData?.group !== 'admin' && (
            <>
          {/* Products Available — farms and markets only */}
          {(postData?.user?.user_type?.toLowerCase() === 'producer' ||
            postData?.user?.user_type?.toLowerCase() === 'market') && (
          <div className="mb-8">
            <h3 className="mb-4 text-lg font-semibold xs:text-base">Products Available</h3>
            <div className="scrollbar-thin max-h-[350px] overflow-y-auto pr-2 xs:pr-0">
              {postData?.post_products?.length === 0 ? (
                <div className="text-gray-500">No products available</div>
              ) : (
                <div className="grid grid-cols-1 gap-3 w-full">
                  {postData?.post_products?.map(({ product }) => (
                    <div
                      key={product.ingredient_id}
                      className="flex items-center gap-3 p-3 transition-shadow border rounded-lg hover:shadow-sm xs:p-2 w-full bg-white"
                    >
                      <div className="relative w-16 h-16 xs:w-12 xs:h-12 flex-shrink-0">
                        <Image
                          src={
                            product.mainImage ||
                            product.illustrationImage ||
                            '/placeholder.svg'
                          }
                          alt={product.name}
                          fill
                          className="object-contain rounded-lg w-full max-w-full"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-gray-900 break-words xs:text-xs">
                          {product.name}
                        </span>
                        {product.description && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2 xs:text-xs">
                            {product.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Recipe Ideas */}
          <div>
          <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold xs:text-base">Recipe Ideas</h3>
              <div className="flex gap-2">
                <button
                  className="p-1 border rounded-full hover:bg-gray-100 xs:p-0.5 xs:w-7 xs:h-7"
                  onClick={() => {
                    const container = document.getElementById(
                      'recipe-scroll-container'
                    )
                    if (container) {
                      container.scrollBy({ left: -300, behavior: 'smooth' })
                    }
                  }}
                >
                  <ChevronLeft className="w-5 h-5 xs:w-4 xs:h-4" />
                </button>
                <button
                  className="p-1 border rounded-full hover:bg-gray-100 xs:p-0.5 xs:w-7 xs:h-7"
                  onClick={() => {
                    const container = document.getElementById(
                      'recipe-scroll-container'
                    )
                    if (container) {
                      container.scrollBy({ left: 300, behavior: 'smooth' })
                    }
                  }}
                >
                  <ChevronRight className="w-5 h-5 xs:w-4 xs:h-4" />
                </button>
              </div>
            </div>

            <div
              id="recipe-scroll-container"
              className="pb-4 overflow-x-auto hide-scrollbar w-full xs:pb-2"
            >
              <div className="flex gap-4 min-w-0 xs:gap-2" style={{ minWidth: 'min-content' }}>
                {postData?.post_recipes?.length === 0 ? (
                  <div className="text-gray-500 xs:text-xs">No recipe ideas</div>
                ) : (
                  postData?.post_recipes?.map(({ recipe }) => (
                    <div
                      key={recipe.recipe_id}
                      className="min-w-[280px] xs:min-w-[200px] cursor-pointer overflow-hidden rounded-lg border transition-shadow hover:shadow-md w-full bg-white"
                      onClick={() =>
                        router.push(`/recipes/${recipe.recipe_id}`)
                      }
                    >
                      <div className="relative w-full h-48 xs:h-20">
                        <Image
                          src={
                            recipe?.resourcePath ||
                            recipe?.recipe_img_url ||
                            recipe.recipe_image?.thumbnail_path ||
                            recipe?.recipe_image?.resource_path ||
                            '/assets/icons/Recipe.svg'
                          }
                          alt={recipe.recipe_name}
                          fill
                          className="object-cover w-full max-w-full"
                        />
                      </div>
                      <div className="p-4 xs:p-2">
                        <h4 className="font-medium text-gray-900 xs:text-xs break-words line-clamp-2">{recipe.recipe_name}</h4>
                        <div className="flex items-center mt-2 xs:mt-1">
                          <div className="flex">
                            {renderStars(recipe.average_recipe_rating)}
                          </div>
                          <span className="ml-2 text-sm text-gray-500 xs:text-xs">
                            {/* @ts-ignore */}
                            ({recipe?.reviews || 0})
                          </span>
                        </div>
                        <div className="mt-2 xs:mt-1 text-sm text-gray-500 xs:text-xs flex items-center gap-2">
                          <span>⏱️ {recipe.total_time}</span>
                          {recipe.number_of_servings && (
                            <span>👥 {recipe.number_of_servings} servings</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
            </>
          )}
        </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Post"
        description="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeletePost}
        variant="destructive"
      />
      <PostShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        post={{
          slug: postData?.slug || '',
          id: postData?.id,
          title: postData?.title,
          content: postData?.content,
          group: postData?.group,
        }}
        userSlug={refSlug}
        hasPhoto={postHasHeroImage(postData)}
        isOwner={isPostOwner}
      />
    </PageContainer>
  )
}
